/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AccordionComponent,
    AccordionGroupComponent,
    ActionDotsComponent,
    ActionDotsWrapperComponent,
    AlgoliaSearchComponent,
    AppCustomPaginatorComponent,
    BurgerMenu30Component,
    CalendlyWidgetComponent,
    ChartComponent,
    ChartNodeComponent,
    CollaborationComponent,
    ConfirmBoxComponent,
    ConfirmPasswordComponent,
    ContentOverlayComponent,
    CsvImporterComponent,
    DrawerComponent,
    DropdownComponent,
    DropdownWrapperComponent,
    EditorComponent,
    ForceLogoutComponent,
    HelpComponent,
    LoadingButtonComponent,
    LogoComponent,
    Menu30Component,
    ModalBodyComponent,
    ModalFooterComponent,
    ModalWrapperComponent,
    NotificationBannerComponent,
    NotificationHeaderWrapperComponent,
    NotificationsBellComponent,
    NotificationToastWrapperComponent,
    ParentModalComponent,
    PhoneNumberComponent,
    PrivateKeyComponent,
    ProgressBarComponent,
    RbacEndpointsComponent,
    RbacGroupComponent,
    RbacRolesComponent,
    RbacTableComponent,
    RbacUsersComponent,
    ReLoginComponent,
    RevealPasswordComponent,
    SearchRowsComponent,
    SplitActionButtonComponent,
    TabComponent,
    TabContainerComponent,
    TableColumnComponent,
    TableComponent,
    TooltipComponent,
    TwoFAComponent,
    TwoFactorAuthAppComponent,
    UploadComponent,
    UsageComponent,
    ValidationMessagesComponent,
    WidgetComponent
} from './components';
import {
    ApiService,
    AuthStoreService,
    ColouriseService,
    ContentInjectionService,
    DragDropService,
    DrawerService,
    KeyStoreService,
    LanguageService,
    LoadingService,
    LogService,
    MenuService,
    ModalService,
    NotifyService,
    PGPService,
    RbacService,
    SocketService,
    StripeService,
    TableService,
    TabService,
    ThemeService,
    TransactionService,
    UserService,
    UtilsService,
    ValidationService
} from './services';
import { AuthApiService, TwoFactorAuthApiService } from './http';
import {
    AutoFocusDirective,
    ClickOutsideDirective,
    CopyToClipboardDirective,
    DomainOrIpValidationDirective,
    DraggableDirective,
    DragNDropDirective,
    DropdownMenuDirective,
    FocusTrapDirective,
    FormDirective,
    HintDirective,
    InputValidatorDirective,
    MaxLengthDirective,
    MenuGroupDirective,
    MenuItemDirective,
    ScreenCaptureDirective,
    SubjectAltNameValidationDirective,
    TableColumnCellDirective
} from './directives';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
    ComplexPasswordValidationDirective,
    CreditCardValidationDirective,
    NumberOnlyDirective,
    PortRangeValidationDirective,
    SimplePasswordValidationDirective,
    UrlValidationDirective
} from './directives/validation';
import { DownloadFileDirective } from './directives/downloadFile';
import { EnvironmentGuard, ExternalPortalOnlyGuard, LoginGuard, PreAuthGuard, RbacPageGuard } from './guards';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LanguagePipe, SafeHtmlPipe, SentenceCasePipe, TruncatePipe, UnderScorePipe } from './pipes';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { NgModule } from '@angular/core';
import { QuillModule } from 'ngx-quill';
import { RouterLink, RouterModule } from '@angular/router';
import { WafOptionsComponent } from '../modules/waf/shared/component/wafOptions/wafOptions.component';

/**
 * Shared imports between pages.
 * Note: You must define a new dep. as an IMPORT/DECLARATION AND EXPORT it!
 */
@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        MatCheckboxModule,
        MatPaginatorModule,
        MatSortModule,
        MatTableModule,
        NgOptimizedImage,
        QuillModule,
        ReactiveFormsModule,
        RouterLink,
        RouterModule
    ],
    declarations: [
        AccordionComponent,
        AccordionGroupComponent,
        ActionDotsComponent,
        ActionDotsWrapperComponent,
        AlgoliaSearchComponent,
        AppCustomPaginatorComponent,
        AutoFocusDirective,
        BurgerMenu30Component,
        CalendlyWidgetComponent,
        ChartComponent,
        ChartComponent,
        ChartNodeComponent,
        ClickOutsideDirective,
        CollaborationComponent,
        ComplexPasswordValidationDirective,
        ConfirmBoxComponent,
        ConfirmPasswordComponent,
        ContentOverlayComponent,
        CopyToClipboardDirective,
        CreditCardValidationDirective,
        CsvImporterComponent,
        DomainOrIpValidationDirective,
        DownloadFileDirective,
        DragNDropDirective,
        DraggableDirective,
        DrawerComponent,
        DropdownComponent,
        DropdownMenuDirective,
        DropdownWrapperComponent,
        EditorComponent,
        FocusTrapDirective,
        ForceLogoutComponent,
        FormDirective,
        HelpComponent,
        HintDirective,
        InputValidatorDirective,
        LanguagePipe,
        LoadingButtonComponent,
        LogoComponent,
        MaxLengthDirective,
        Menu30Component,
        MenuGroupDirective,
        MenuItemDirective,
        ModalBodyComponent,
        ModalFooterComponent,
        ModalWrapperComponent,
        NotificationBannerComponent,
        NotificationHeaderWrapperComponent,
        NotificationToastWrapperComponent,
        NotificationsBellComponent,
        NumberOnlyDirective,
        ParentModalComponent,
        PhoneNumberComponent,
        PortRangeValidationDirective,
        PrivateKeyComponent,
        ProgressBarComponent,
        RbacEndpointsComponent,
        RbacGroupComponent,
        RbacRolesComponent,
        RbacTableComponent,
        RbacUsersComponent,
        ReLoginComponent,
        RevealPasswordComponent,
        SafeHtmlPipe,
        ScreenCaptureDirective,
        SearchRowsComponent,
        SentenceCasePipe,
        SimplePasswordValidationDirective,
        SplitActionButtonComponent,
        SubjectAltNameValidationDirective,
        TabComponent,
        TabContainerComponent,
        TableColumnCellDirective,
        TableColumnComponent,
        TableComponent,
        TooltipComponent,
        TruncatePipe,
        TwoFAComponent,
        TwoFactorAuthAppComponent,
        UnderScorePipe,
        UploadComponent,
        UrlValidationDirective,
        UsageComponent,
        ValidationMessagesComponent,
        WafOptionsComponent,
        WidgetComponent,
    ],
    exports: [
        // Imports
        CommonModule,
        FormsModule,
        MatTableModule,
        NgOptimizedImage,
        ReactiveFormsModule,
        RouterLink,
        RouterModule,
        // Declarations
        AccordionComponent,
        AccordionGroupComponent,
        ActionDotsComponent,
        ActionDotsWrapperComponent,
        AlgoliaSearchComponent,
        AppCustomPaginatorComponent,
        AutoFocusDirective,
        BurgerMenu30Component,
        CalendlyWidgetComponent,
        ChartComponent,
        ConfirmBoxComponent,
        ConfirmPasswordComponent,
        ContentOverlayComponent,
        CopyToClipboardDirective,
        CreditCardValidationDirective,
        CsvImporterComponent,
        DomainOrIpValidationDirective,
        DragNDropDirective,
        DraggableDirective,
        DrawerComponent,
        DropdownComponent,
        EditorComponent,
        FocusTrapDirective,
        ForceLogoutComponent,
        FormDirective,
        HelpComponent,
        HintDirective,
        InputValidatorDirective,
        LanguagePipe,
        LoadingButtonComponent,
        LogoComponent,
        MaxLengthDirective,
        Menu30Component,
        MenuGroupDirective,
        MenuItemDirective,
        ModalBodyComponent,
        ModalFooterComponent,
        ModalWrapperComponent,
        NotificationBannerComponent,
        NotificationHeaderWrapperComponent,
        NotificationToastWrapperComponent,
        NotificationsBellComponent,
        NumberOnlyDirective,
        ParentModalComponent,
        PhoneNumberComponent,
        PortRangeValidationDirective,
        PrivateKeyComponent,
        ProgressBarComponent,
        RbacEndpointsComponent,
        RbacGroupComponent,
        RbacRolesComponent,
        RbacTableComponent,
        RbacUsersComponent,
        ReLoginComponent,
        RevealPasswordComponent,
        SafeHtmlPipe,
        ScreenCaptureDirective,
        SearchRowsComponent,
        SentenceCasePipe,
        SplitActionButtonComponent,
        SubjectAltNameValidationDirective,
        TabComponent,
        TabContainerComponent,
        TableColumnCellDirective,
        TableColumnComponent,
        TableComponent,
        TooltipComponent,
        TruncatePipe,
        TwoFAComponent,
        TwoFactorAuthAppComponent,
        UnderScorePipe,
        UploadComponent,
        UrlValidationDirective,
        UsageComponent,
        ValidationMessagesComponent,
        WidgetComponent
    ],
    providers: [
        ApiService,
        AuthApiService,
        AuthStoreService,
        ColouriseService,
        ContentInjectionService,
        DragDropService,
        DrawerService,
        EnvironmentGuard,
        ExternalPortalOnlyGuard,
        KeyStoreService,
        LanguageService,
        LoadingService,
        LogService,
        LoginGuard,
        MenuService,
        ModalService,
        NotifyService,
        PGPService,
        PreAuthGuard,
        RbacPageGuard,
        RbacService,
        StripeService,
        SocketService,
        TabService,
        TableService,
        ThemeService,
        TransactionService,
        TwoFactorAuthApiService,
        UserService,
        UtilsService,
        ValidationService,
    ]
})

export class SharedModule {
}
